import { z, ZodIssueCode } from 'zod'
import {
  createNonEmptyStringSchema,
  createPhoneNumberSchema,
  fieldNotValid,
  getTranslationMessage,
  notRequiredSchema,
  optionalField,
  TranslationFunc,
} from '../common'
import {
  createAddressSchema,
  createFirstNameSchema,
  createLastNameSchema,
} from './traveler-details'

export const PaymentMethodSchema = z.enum(['ApplePay', 'CardPayment', 'FullAvios'])
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>

const emailRegex =
  /^(?=.{1,64}@)[A-Za-z0-9+_-]+(.[A-Za-z0-9+_-]+)*@[^-][A-Za-z0-9-+]+(.[A-Za-z0-9-+]+)*(.[A-Za-z]{2,})$/

const createBillingDetailsSchema = (t?: TranslationFunc) =>
  z.object({
    firstName: createFirstNameSchema(t),
    familyName: createLastNameSchema(t),
    phone: createPhoneNumberSchema(t),
    emailAddress: createNonEmptyStringSchema({
      field: 'common.forms.labels.email_address',
      t,
    }).regex(
      emailRegex,
      getTranslationMessage({ key: fieldNotValid, field: 'common.forms.labels.email_address', t })
    ),
    address: createAddressSchema(t),
  })

export type BillingDetails = z.infer<ReturnType<typeof createBillingDetailsSchema>>

type IsCreditCardExpireDateValidParams = {
  expiryMonth: string
  expiryYear: string
  ctx: z.RefinementCtx
  t?: TranslationFunc
}

const isCreditCardExpireDateValid = ({
  expiryMonth,
  expiryYear,
  ctx,
  t,
}: IsCreditCardExpireDateValidParams) => {
  const today = new Date()
  const cardExpiryDate = new Date(Number(expiryYear), +Number(expiryMonth) - 1, 1)

  if (
    cardExpiryDate.getFullYear() < today.getFullYear() ||
    (cardExpiryDate.getFullYear() === today.getFullYear() &&
      cardExpiryDate.getMonth() < today.getMonth())
  ) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      path: ['expiryMonth'],
      message:
        getTranslationMessage({
          key: fieldNotValid,
          field: 'common.forms.labels.expiry_month',
          t,
        })?.message ?? 'Invalid expiry date',
    })
  }
}

export const createCreditCardInformationSchema = (
  isExtendedPaymentInformationForm: boolean,
  t?: TranslationFunc
) =>
  z
    .object({
      expiryMonth: createNonEmptyStringSchema({
        field: 'pages.orders.payment.microform.expiry_month',
        t,
      }),
      expiryYear: createNonEmptyStringSchema({
        field: 'pages.orders.payment.microform.expiry_year',
        t,
      }),

      isCreditCardNumberValid: z.literal(true),
      isSecurityCodeValid: z.literal(true),
      captureContext: optionalField(
        isExtendedPaymentInformationForm ? z.string().min(1) : notRequiredSchema
      ),
      creditCardToken: optionalField(
        isExtendedPaymentInformationForm ? z.string().min(1) : notRequiredSchema
      ),
    })
    .superRefine((val, ctx) => {
      const { expiryMonth, expiryYear } = val
      isCreditCardExpireDateValid({ expiryMonth, expiryYear, ctx, t })
    })

export type CreatePaymentInformationFormSchemaParams = {
  isExtendedPaymentInformationForm: boolean
  isFeesPoliciesAndTermsAndConditionsExternalRequired: boolean
  isPTLTAStatementRequired: boolean
}

export const createBasePaymentInformationFields = (
  {
    isFeesPoliciesAndTermsAndConditionsExternalRequired,
    isPTLTAStatementRequired,
  }: CreatePaymentInformationFormSchemaParams,
  t?: TranslationFunc
) => {
  const mustAgreeToTermsAndConditionsMessage =
    getTranslationMessage({
      key: 'pages.orders.payment.validation.agree_to_terms',
      t,
    })?.message ?? 'You must agree to the terms and conditions'

  const mustAgreeToTermsAndConditionsParams = {
    error: mustAgreeToTermsAndConditionsMessage,
  }

  return {
    deviceCode: z.string().optional(),
    PTLTAStatement: optionalField(
      isPTLTAStatementRequired
        ? z.literal(true, mustAgreeToTermsAndConditionsParams)
        : notRequiredSchema
    ),
    feesPoliciesAndTermsAndConditions: z.literal(true, mustAgreeToTermsAndConditionsParams),
    feesPoliciesAndTermsAndConditionsExternal: optionalField(
      isFeesPoliciesAndTermsAndConditionsExternalRequired
        ? z.literal(true, mustAgreeToTermsAndConditionsParams)
        : notRequiredSchema
    ),
  }
}

const createApplePayPaymentDetailsSchema = (isExtendedPaymentInformationForm: boolean) => {
  if (!isExtendedPaymentInformationForm) {
    return notRequiredSchema
  }
  return z.object({
    cardToken: z.string().min(1),
  })
}

export const createPaymentInformationFormSchema = (
  params: CreatePaymentInformationFormSchemaParams,
  t?: TranslationFunc
) => {
  const { isExtendedPaymentInformationForm } = params
  const baseFields = createBasePaymentInformationFields(params, t)

  return z.discriminatedUnion('paymentMethod', [
    z.object({
      paymentMethod: z.literal(PaymentMethodSchema.enum.CardPayment),
      ...baseFields,
      data: z.object({
        billingDetails: createBillingDetailsSchema(t),
        creditCardDetails: createCreditCardInformationSchema(isExtendedPaymentInformationForm, t),
      }),
    }),
    z.object({
      paymentMethod: z.literal(PaymentMethodSchema.enum.ApplePay),
      ...baseFields,
      data: z.object({
        billingDetails: optionalField(
          isExtendedPaymentInformationForm ? createBillingDetailsSchema(t) : notRequiredSchema
        ),
        applePayDetails: optionalField(
          createApplePayPaymentDetailsSchema(isExtendedPaymentInformationForm)
        ),
      }),
    }),
    z.object({
      paymentMethod: z.literal(PaymentMethodSchema.enum.FullAvios),
      ...baseFields,
    }),
  ])
}

export type PaymentInfoSubmitOrderForm = z.infer<
  ReturnType<typeof createPaymentInformationFormSchema>
>

export type PaymentInformationForm = z.infer<ReturnType<typeof createPaymentInformationFormSchema>>
